import React, { useEffect, useRef, useState } from 'react';
import { useAccess } from '../access/AccessContext';
import { useSessionOperationReady } from '../lib/SessionOperationContext';
import { publishPortfolioSnapshot, readPublicationOperation, discoverPublicationOperation, acknowledgePublicationOperation, recoverPublicationOperation } from '../services/portfolioSnapshotApi';
import GovernedNavigation from './GovernedNavigation';

export default function ProgramEvidencePublication({ program, payload, ready, children }) {
  const access = useAccess();
  const canSend = useSessionOperationReady();
  const key = `rigo:program-publication:${encodeURIComponent(access.username || '')}:${program.program_reference}`;
  const [review, setReview] = useState(false);
  const [state, setState] = useState('DISCOVERING');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [operation, setOperation] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [poll, setPoll] = useState(0);
  const lock = useRef(true);
  const mounted = useRef(false);
  const acknowledging = useRef(false);
  const recovering = useRef(false);
  const rejected = useRef(false);
  const intent = useRef(null);
  const scope = `${key}:${canSend.generation ?? ''}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const validIntent = item => mounted.current && intent.current === item
    && item.scope === currentScope.current && (!item.guard.isCurrent || item.guard.isCurrent());
  const cancelUnsent = () => {
    const item = intent.current;
    if (item?.sent) return false;
    if (item?.id) item.forget(item.id);
    intent.current = null;
    return true;
  };
  // Hints improve same-tab UX; server discovery/reservation owns correctness.
  const remember = id => { try { sessionStorage.setItem(key, id); } catch { /* Server recovery is authoritative. */ } };
  const forget = id => { try { if (!id || sessionStorage.getItem(key) === id) sessionStorage.removeItem(key); } catch { /* No server identity is lost. */ } };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancelUnsent(); };
    // Cleanup only invalidates this mounted instance's unsent intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    cancelUnsent();
    intent.current = null; // Sent operations remain discoverable in their own scope.
    lock.current = true; setReview(false); setMessage('');
    setOperation(null); setResult(null); setPrevious(null); setState('DISCOVERING');
    // A new identity must discover its own server lifecycle, never inherit intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  useEffect(() => {
    if (!['DISCOVERING', 'RECONCILING'].includes(state)) return undefined;
    let active = true, timer, attempts = 0;
    const reconcile = async () => {
      attempts += 1;
      try {
        let data = state === 'DISCOVERING' || !operation
          ? await discoverPublicationOperation(program.program_reference)
          : await readPublicationOperation(operation, program.program_reference);
        if (state === 'RECONCILING' && data.operation_status === 'RECONCILING') {
          const discovered = await discoverPublicationOperation(program.program_reference);
          if (discovered.operation_status !== 'NONE') data = discovered;
        }
        if (!active) return;
        if (data.operation_status === 'NONE') {
          // Initial server discovery gates ordinary creation. A concurrent unseen
          // reservation is protected by the server's one-open-lifecycle constraint.
          // This authoritative fresh-lifecycle transition also retires historical
          // intent: another tab may have advanced the acknowledged chain tip.
          // Unresolved sends never reach here on discovery NONE (see above).
          intent.current = null;
          forget(); setOperation(null); setResult(null); setPrevious(data.previous_operation_id || null); lock.current = false;
          setState(rejected.current ? 'CONFIRMED_FAILURE' : 'IDLE');
          rejected.current = false; return;
        }
        if (data.operation_id) { remember(data.operation_id); setOperation(data.operation_id); }
        if (data.operation_status === 'RECOVERY_REQUIRED' && data.execution_attempt_id) {
          setResult(data); setState('RECOVERY_REQUIRED'); setMessage(''); return;
        }
        if (data.operation_status === 'SUCCESS' && data.id && data.analytical_evidence_id) {
          rejected.current = false; setResult(data); setState('SUCCESS'); setMessage(''); return;
        }
        if (data.operation_status === 'CONFIRMED_FAILURE') {
          rejected.current = false; setResult(data); setState('CONFIRMED_FAILURE'); setMessage(data.detail || 'Publication was not committed.'); return;
        }
      } catch { /* No inference of non-commit from authentication/transport errors. */ }
      if (!active) return;
      if (attempts < 5) timer = setTimeout(reconcile, 2000);
      else setMessage('Publication status is still unresolved. Creation remains locked. Sign in if needed, then check status.');
    };
    reconcile();
    return () => { active = false; clearTimeout(timer); };
    // Correlation helpers deliberately operate only on the captured actor/Program key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, operation, program.program_reference, poll, key, scope]);
  const create = async () => {
    if (lock.current) return;
    if (!ready) { setMessage('Analysis inputs are still loading. Publication has not started.'); return; }
    lock.current = true; setState('ACCEPTED'); setMessage('');
    const item = { scope, guard: canSend, payload: { ...payload }, previous, id: null, sent: false, forget };
    intent.current = item;
    const waitUntilReady = async () => {
      if (!validIntent(item)) return false;
      setState('WAITING_FOR_SESSION');
      try {
        const verified = await item.guard.ensureReady?.();
        return Boolean(verified && validIntent(item) && item.guard());
      } catch { return false; }
    };
    let id;
    try {
      if (!item.guard() && !await waitUntilReady()) {
        throw Object.assign(new Error('Session validity could not be confirmed. No publication was sent.'), { notSent: true });
      }
      if (!validIntent(item)) return;
      id = window.crypto.randomUUID(); item.id = id;
      remember(id); setOperation(id); setState('PREPARING');
      const data = await publishPortfolioSnapshot({ ...item.payload, operation_id: id, previous_operation_id: item.previous },
        () => validIntent(item) && item.guard(), {
          waitUntilReady,
          onSending: () => { item.sent = true; setState('PROCESSING'); },
        });
      if (!validIntent(item)) return;
      item.sent = true;
      if (data?.operation_id && ['PROCESSING', 'RECOVER'].includes(data.operation_status)) {
        remember(data.operation_id); setOperation(data.operation_id); setState('RECONCILING'); return;
      }
      if (data?.operation_status !== 'SUCCESS' || data.operation_id !== id || !data.id || !data.analytical_evidence_id) throw new Error('Unverified response');
      setResult(data); setState('SUCCESS'); setReview(false);
    } catch (error) {
      // Correlation cleanup is NOT a React lifecycle operation. Never discard a
      // newer attempt's UUID when an older asynchronous pre-send failure arrives.
      if (error.notSent && id) forget(id);
      if (!validIntent(item)) return;
      if (error.notSent) {
        intent.current = null; setOperation(null); setState('BLOCKED'); setMessage(error.message);
      } else if (!id) {
        intent.current = null; setOperation(null); setState('BLOCKED'); setMessage('Publication preparation failed. No publication was sent.');
      } else if (error.confirmedFailure) {
        setState('CONFIRMED_FAILURE'); setMessage(error.message);
      } else if (error.rejected) {
        // Check for a previous receipt before treating pre-claim rejection as a
        // new-attempt failure; a conflicting existing operation must be recovered.
        rejected.current = true; setMessage(error.message); setState('DISCOVERING');
      } else { setState('RECONCILING'); setMessage(''); }
    }
  };
  const cancelReview = () => {
    if (!cancelUnsent()) return;
    lock.current = false; setOperation(null); setMessage(''); setReview(false); setState('IDLE');
  };
  const recover = async () => {
    if (recovering.current || state !== 'RECOVERY_REQUIRED') return;
    if (!canSend()) { setMessage('Waiting for verified session validity. Recovery has not been sent.'); return; }
    recovering.current = true; setState('PROCESSING'); setMessage('');
    try {
      const data = await recoverPublicationOperation(operation, program.program_reference, result.execution_attempt_id, canSend);
      if (!mounted.current) return;
      if (data.operation_status === 'SUCCESS' && data.id && data.analytical_evidence_id) {
        setResult(data); setState('SUCCESS'); setReview(false);
      } else { setState('RECONCILING'); }
    } catch (error) {
      if (!mounted.current) return;
      setMessage(error.notSent ? error.message : '');
      setState(error.notSent ? 'RECOVERY_REQUIRED' : 'RECONCILING');
    } finally { recovering.current = false; }
  };
  const newOperation = async () => {
    if (acknowledging.current) return;
    if (!canSend()) { setMessage('Waiting for verified session validity. No acknowledgement was sent.'); return; }
    acknowledging.current = true;
    lock.current = true; setState('ACKNOWLEDGING'); setMessage('');
    try {
      if (operation) await acknowledgePublicationOperation(operation, program.program_reference, canSend);
      if (!mounted.current) return;
      forget(operation); intent.current = null; setOperation(null); setResult(null); setReview(true); setState('DISCOVERING');
    } catch (error) {
      if (!mounted.current) return;
      // Lost acknowledgement response is resolved by discovery, never publication.
      setMessage(error.message); setState('DISCOVERING');
    } finally { acknowledging.current = false; }
  };
  const unsent = ['WAITING_FOR_SESSION', 'PREPARING', 'ACCEPTED'].includes(state);
  const busy = unsent || ['PROCESSING', 'RECONCILING', 'DISCOVERING', 'ACKNOWLEDGING'].includes(state);
  return <section aria-label="Program evidence publication" aria-busy={busy}>
    {message && <p role="alert">{message}</p>}
    {state === 'IDLE' && <button type="button" disabled={!ready} onClick={() => setReview(true)}>Publish as Program Current Evidence</button>}
    {review && state === 'IDLE' && <section role="dialog" aria-label="Review Program current evidence">
      <h2>Review Program Current Evidence</h2>{children}
      <p>Displayed numbers are provisional preview results. Publication performs a fresh calculation from active persisted scenarios; its exact capture is the governed evidence and may differ from this preview. The snapshot will be DRAFT and require separate approval.</p>
      <button type="button" onClick={create}>Create Draft Evidence</button>
      <button type="button" onClick={cancelReview}>Cancel</button>
    </section>}
    {unsent && <><p role="status">{state === 'WAITING_FOR_SESSION'
      ? 'Waiting for verified session validity. Your intent is retained; no publication has been sent.'
      : 'Preparing publication. No publication has been sent.'}</p><button type="button" disabled>Create Draft Evidence</button><button type="button" onClick={cancelReview}>Cancel</button></>}
    {state === 'BLOCKED' && <><p role="status">Publication intent stopped. No publication was sent.</p><button type="button" onClick={cancelReview}>Cancel</button></>}
    {state === 'PROCESSING' && <><p role="status">Creating governed evidence…</p><button type="button" disabled>Create Draft Evidence</button></>}
    {state === 'RECOVERY_REQUIRED' && <section role="status"><p>Previous publication did not complete. No execution currently owns it.</p><p>Recover the same publication explicitly. Recovery performs a fresh exact calculation; approval remains separate.</p><button type="button" onClick={recover}>Recover publication</button></section>}
    {state === 'ACKNOWLEDGING' && <p role="status">Acknowledging previous publication…</p>}
    {state === 'DISCOVERING' && <p role="status">Checking previous publication…</p>}
    {['DISCOVERING', 'RECONCILING'].includes(state) && <>{state === 'RECONCILING' && <p role="status">Verifying publication status…</p>}<button type="button" onClick={() => { setMessage(''); setPoll(n => n + 1); }}>Check publication status</button></>}
    {state === 'CONFIRMED_FAILURE' && <><p role="status">Draft evidence was not created for this operation.</p><button type="button" onClick={newOperation}>Retry with a new operation</button></>}
    {state === 'SUCCESS' && result && <section role="status">
      <h2>Draft evidence created successfully.</h2>
      <p>PortfolioSnapshot #{result.id} · Captured status: {String(result.status).toUpperCase()}</p>
      <p>Program: {program.program_name} · {program.program_reference}</p>
      <p>Analytical evidence: {result.analytical_evidence_id} · Capture: {result.capture_event_id}</p>
      <p>Publication time: {result.snapshot_date}</p>
      <p>Evidence captured. Approval is separate and was not performed by this action.</p>
      {result.current_snapshot_status && <p>Current snapshot status: {result.current_snapshot_status}</p>}
      <GovernedNavigation to={`/benefits-governance/${encodeURIComponent(program.program_reference)}?evidence_snapshot=${encodeURIComponent(result.id)}`} pendingText="Opening Benefits Governance…">Review / Approve Evidence</GovernedNavigation>
      <button type="button" onClick={newOperation}>Review a new deliberate publication</button>
    </section>}
  </section>;
}
